import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logger } from '../../../shared/utils/logger';

export interface Finca {
  id: string;
  numeroFinca: number; // Número de finca que no cambia al eliminar otras
  nombre: string;
  fechaCreacion: Date;
  fechaModificacion: Date;
}

export interface FincasState {
  fincas: Finca[];
  initializeFincas: () => void;
  createFinca: (fincaData: Omit<Finca, 'id' | 'numeroFinca' | 'fechaCreacion' | 'fechaModificacion'>) => Finca;
  createFincaWithNumber: (numeroFinca: number, nombre?: string) => Finca | null;
  updateFinca: (id: string, updates: Partial<Omit<Finca, 'id' | 'numeroFinca' | 'fechaCreacion'>>) => Finca | null;
  deleteFinca: (id: string) => boolean;
  getFincaById: (id: string) => Finca | null;
  getAllFincas: () => Finca[];
  getNextAvailableNumber: () => number;
  exportToCSV: () => string;
  importFromCSV: (csvContent: string) => { success: number; errors: string[] };
  resetToDefaults: () => void;
  cleanDuplicates: () => boolean;
  validateIntegrity: () => boolean;
}

export const useFincasStore = create<FincasState>()(
  persist(
    (set, get) => ({
      fincas: [],

      initializeFincas: () => {
        const { fincas } = get();
        if (fincas.length === 0) {
          const now = new Date();
          const defaultFincas: Finca[] = Array.from({ length: 10 }, (_, index) => ({
            id: `finca_${Date.now()}_${index}`,
            numeroFinca: index + 1, // Numeración fija del 1 al 10
            nombre: '', // Nombres vacíos inicialmente
            fechaCreacion: now,
            fechaModificacion: now
          }));
          set({ 
            fincas: defaultFincas
          });
          logger.log('FINCAS_STORE', '✅ FincasStore: 10 fincas iniciales creadas con numeración fija');
        } else {
          // Migración: Si las fincas existentes no tienen numeroFinca, asignarles uno
          const fincasConNumeracion = fincas.map((finca, index) => {
            if (!('numeroFinca' in finca)) {
              return {
                ...finca,
                numeroFinca: index + 1
              };
            }
            return finca;
          });
          
          // Si se hizo migración, actualizar el estado
          const necesitaMigracion = fincas.some(f => !('numeroFinca' in f));
          if (necesitaMigracion) {
            set({ fincas: fincasConNumeracion });
            logger.log('FINCAS_STORE', '✅ FincasStore: Migración de numeración completada');
          }
          
          // No necesitamos calcular nextFincaNumber ya que ahora se calcula dinámicamente
        }
      },

      createFinca: (fincaData) => {
        const { fincas } = get();
        
        // Encontrar el primer número de finca disponible
        const numerosExistentes = fincas.map(f => f.numeroFinca).sort((a, b) => a - b);
        let primerNumeroDisponible = 1;
        
        for (const numero of numerosExistentes) {
          if (numero === primerNumeroDisponible) {
            primerNumeroDisponible++;
          } else {
            break; // Encontramos el primer hueco
          }
        }
        
        const nuevaFinca: Finca = {
          ...fincaData,
          id: `finca_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          numeroFinca: primerNumeroDisponible, // Usar el primer número disponible
          fechaCreacion: new Date(),
          fechaModificacion: new Date()
        };

        set((state) => ({
          fincas: [...state.fincas, nuevaFinca]
        }));

        logger.log('FINCAS_STORE', '✅ FincasStore: Nueva finca creada:', nuevaFinca);
        return nuevaFinca;
      },

      // Crear finca con un número específico si no existe
      createFincaWithNumber: (numeroFinca, nombre) => {
        const { fincas } = get();
        if (!numeroFinca || numeroFinca <= 0) {
          console.warn('⚠️ FincasStore: Número de finca inválido:', numeroFinca);
          return null;
        }
        const numeroExiste = fincas.some(f => f.numeroFinca === numeroFinca);
        if (numeroExiste) {
          logger.log('FINCAS_STORE', 'ℹ️ FincasStore: Número de finca ya existente, no se crea:', numeroFinca);
          return null;
        }
        const nuevaFinca: Finca = {
          id: `finca_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          numeroFinca,
          nombre: nombre || '',
          fechaCreacion: new Date(),
          fechaModificacion: new Date(),
        };
        set((state) => ({ fincas: [...state.fincas, nuevaFinca] }));
        logger.log('FINCAS_STORE', '✅ FincasStore: Nueva finca creada con número específico:', nuevaFinca);
        return nuevaFinca;
      },

      updateFinca: (id, updates) => {
        const { fincas } = get();
        const index = fincas.findIndex(f => f.id === id);
        
        if (index === -1) {
          console.warn('⚠️ FincasStore: Finca no encontrada para actualizar:', id);
          return null;
        }

        // Si se está actualizando el numeroFinca, verificar que no exista duplicado
        if (updates.numeroFinca !== undefined) {
          const numeroExiste = fincas.some(f => 
            f.id !== id && f.numeroFinca === updates.numeroFinca
          );
          
          if (numeroExiste) {
            console.warn('⚠️ FincasStore: El número de finca ya existe:', updates.numeroFinca);
            return null;
          }
        }

        const updatedFinca = {
          ...fincas[index],
          ...updates,
          fechaModificacion: new Date()
        };

        set((state) => ({
          fincas: state.fincas.map(f => f.id === id ? updatedFinca : f)
        }));

        logger.log('FINCAS_STORE', '✅ FincasStore: Finca actualizada:', updatedFinca);
        return updatedFinca;
      },

      deleteFinca: (id) => {
        const { fincas } = get();
        const index = fincas.findIndex(f => f.id === id);
        
        if (index === -1) {
          console.warn('⚠️ FincasStore: Finca no encontrada para eliminar:', id);
          return false;
        }

        // NO renumerar las fincas restantes, solo eliminar la seleccionada
        set((state) => ({
          fincas: state.fincas.filter(f => f.id !== id)
        }));

        logger.log('FINCAS_STORE', '✅ FincasStore: Finca eliminada:', id);
        return true;
      },

      getFincaById: (id) => {
        const { fincas } = get();
        return fincas.find(f => f.id === id) || null;
      },

      getAllFincas: () => {
        const { fincas } = get();
        return [...fincas];
      },

      getNextAvailableNumber: () => {
        const { fincas } = get();
        
        // Encontrar el primer número de finca disponible
        const numerosExistentes = fincas.map(f => f.numeroFinca).sort((a, b) => a - b);
        let primerNumeroDisponible = 1;
        
        for (const numero of numerosExistentes) {
          if (numero === primerNumeroDisponible) {
            primerNumeroDisponible++;
          } else {
            break; // Encontramos el primer hueco
          }
        }
        
        return primerNumeroDisponible;
      },

      exportToCSV: () => {
        const { fincas } = get();
        const headers = ['ID', 'Número Finca', 'Nombre', 'Fecha Creación', 'Fecha Modificación'];
        const rows = fincas.map(f => [
          f.id,
          f.numeroFinca.toString(),
          f.nombre,
          f.fechaCreacion.toISOString().split('T')[0],
          f.fechaModificacion.toISOString().split('T')[0]
        ]);

        const csvContent = [headers, ...rows]
          .map(row => row.map(cell => `"${cell}"`).join(','))
          .join('\n');

        return csvContent;
      },

      importFromCSV: (csvContent) => {
        const lines = csvContent.split('\n').filter(line => line.trim());
        const errors: string[] = [];
        let success = 0;

        // Saltar la primera línea (headers)
        for (let i = 1; i < lines.length; i++) {
          try {
            const line = lines[i];
            const cells = line.split(',').map(cell => cell.replace(/^"|"$/g, '').trim());
            
            if (cells.length < 3) {
              errors.push(`Línea ${i + 1}: Número insuficiente de columnas`);
              continue;
            }

            const numeroFinca = parseInt(cells[1]) || 0;
            const fincaData = {
              nombre: cells[2] || ''
            };

            // Verificar si el número de finca ya existe
            const { fincas } = get();
            if (numeroFinca > 0) {
              const numeroExiste = fincas.some(f => f.numeroFinca === numeroFinca);
              if (numeroExiste) {
                errors.push(`Línea ${i + 1}: El número de finca ${numeroFinca} ya existe`);
                continue;
              }
            }

            // Si se especifica un número de finca, usarlo; si no, usar el primer disponible
            if (numeroFinca > 0) {
              // Crear finca con número específico
              const nuevaFinca: Finca = {
                ...fincaData,
                id: `finca_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                numeroFinca: numeroFinca,
                fechaCreacion: new Date(),
                fechaModificacion: new Date()
              };

              set((state) => ({
                fincas: [...state.fincas, nuevaFinca]
              }));
            } else {
              // Usar el primer número disponible
              get().createFinca(fincaData);
            }
            
            success++;
          } catch (error) {
            errors.push(`Línea ${i + 1}: ${error}`);
          }
        }

        logger.log('FINCAS_STORE', `📊 FincasStore: Importación completada - ${success} exitosas, ${errors.length} errores`);
        return { success, errors };
      },

      resetToDefaults: () => {
        const now = new Date();
        const defaultFincas: Finca[] = Array.from({ length: 10 }, (_, index) => ({
          id: `finca_${Date.now()}_${index}`,
          numeroFinca: index + 1, // Numeración fija del 1 al 10
          nombre: '', // Nombres vacíos
          fechaCreacion: now,
          fechaModificacion: now
        }));
        
        set({ 
          fincas: defaultFincas
        });
        logger.log('FINCAS_STORE', '✅ FincasStore: Fincas reseteadas a valores por defecto');
      },

      // Función para limpiar duplicados existentes
      cleanDuplicates: () => {
        const { fincas } = get();
        
        // Mantener solo la primera ocurrencia de cada numeroFinca
        const fincasUnicas = fincas.filter((finca, index, self) => 
          index === self.findIndex(f => f.numeroFinca === finca.numeroFinca)
        );
        
        if (fincasUnicas.length !== fincas.length) {
          set({ fincas: fincasUnicas });
          logger.log('FINCAS_STORE', `✅ FincasStore: Duplicados eliminados. Antes: ${fincas.length}, Después: ${fincasUnicas.length}`);
          return true;
        }
        
        logger.log('FINCAS_STORE', '✅ FincasStore: No se encontraron duplicados');
        return false;
      },

      // Función para verificar integridad del store
      validateIntegrity: () => {
        const { fincas } = get();
        const numeros = fincas.map(f => f.numeroFinca);
        const duplicados = numeros.filter((numero, index) => numeros.indexOf(numero) !== index);
        
        if (duplicados.length > 0) {
          console.warn('⚠️ FincasStore: Se encontraron números duplicados:', duplicados);
          return false;
        }
        
        logger.log('FINCAS_STORE', '✅ FincasStore: Integridad verificada - sin duplicados');
        return true;
      }
    }),
    {
      name: 'catastral-fincas-storage',
      partialize: (state) => ({
        fincas: state.fincas
      }),
    }
  )
);
